import React from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { ContextMenu } from '@producer/ui-kit';

import { buildStepMenuItems, type StepMenuUpdate } from './step-menu-items';
import { Bar } from '../../../components/gantt/view/bar';
import { DeleteDialog } from '../../../components/dialogs/DeleteDialog';
import { openDateSelectionModal } from '../../../components/date-selector/open-date-selection-modal';
import { openModal } from '../../../core/modal/open-modal';
import { RenameStepModal } from '../../../features/process/components/rename-step.modal';
import { Toast } from '../../../features/toasts/models/toast';
import { toastPool } from '../../../features/toasts/models/toast-pool';

import type { Step } from '../../../app/entities/step';
import type { BarRendererProps } from '../../../components/gantt/types';

/**
 * Both calendar surfaces tag their step rows the same way — `project-calendar.route.tsx`'s
 * `CalendarRowData` and `multi-projects-calendar.page.tsx`'s `SpaceRowData` share this variant —
 * which is what lets one bar renderer serve both without either union leaking in here.
 */
type StepRowData = { kind: 'step'; step: Step; isViewOnly?: boolean };

const isStepRow = (data: unknown): data is StepRowData =>
  !!data && typeof data === 'object' && (data as { kind?: string }).kind === 'step';

/**
 * A menu edit that failed is invisible otherwise: `Model.update` merges into the entity only once
 * the call resolves, so a rejection leaves the bar exactly as it was with nothing to explain why.
 * The API's own message is preferred where it has one — an inverted date range comes back as
 * `Invalid start or due date` from `update-step.ts`, which says more than any generic sentence
 * this could invent — with the same `toastPool`/`Toast` pair the drag path already uses
 * (`commitGanttPatches`).
 */
const reportFailure = (error: unknown) => {
  const message = (error as { response?: { data?: { message?: string } } })?.response?.data
    ?.message;

  toastPool.insert(new Toast(message || 'Could not save the change. Please try again.', 'error'));
};

/**
 * PRO-1073. `renderers.bar` for the Calendar view: the default `Bar`, wrapped in the right-click
 * menu that makes a step editable without leaving the view.
 *
 * MODULE SCOPE IS LOAD-BEARING. `row-list.tsx` renders this as an element TYPE, so a component
 * declared inside a route's render body would get a fresh identity on every render — every visible
 * bar would unmount and remount on any MobX-observed change (a teammate's remote edit, a status
 * write from a neighbouring row), closing whatever menu the viewer had open. `RowHeaderRenderer`
 * carries the same constraint for the same reason; see `project-calendar.route.tsx`.
 *
 * `observer`, so a menu already open re-reads the step it was built from: a status written from
 * the menu, or a date a teammate moved, changes which entries the next open should offer.
 *
 * Right-click never conflicts with the drag gesture — `useBarDrag` ignores any non-primary button
 * (`use-bar-drag.ts`) — and `Bar` composes the menu's handlers with its own rather than letting
 * either replace the other (see `BarProps`).
 */
export const CalendarStepBar: React.FC<BarRendererProps> = observer((props) => {
  const navigate = useNavigate();
  const data = props.row.item.data;

  // Group rows (a stage, or a project in the space calendar) render through `renderers.groupBar`,
  // so in practice this only ever sees steps. Kept as a real fallback rather than an assertion:
  // an unrecognised row still has to draw its bar.
  if (!isStepRow(data)) return <Bar {...props} />;

  const { step, isViewOnly } = data;

  const update = async (values: StepMenuUpdate) => {
    try {
      await step.update(values);
    } catch (error) {
      reportFailure(error);
    }
  };

  const items = buildStepMenuItems({
    step,
    isViewOnly,
    collaborators: step.project?.collaborators ?? [],
    handlers: {
      onOpen: () =>
        navigate(`/space/${step.project?.spaceId}/projects/${step.projectId}/steps/${step._id}`),
      onUpdate: (values) => void update(values),
      onRename: () => openModal(RenameStepModal, { step }),
      // The sibling field bounds the picker, so an inverted range is refused in the modal rather
      // than by the API — `reportFailure` above stays the backstop for one that races a remote
      // edit. `openDateSelectionModal` is the same entry point the step's own property button
      // uses, so a date set from the Calendar view and one set from Overview cannot diverge.
      onEditDate: (field) =>
        openDateSelectionModal({
          field,
          value: step[field],
          ...(field === 'startDate' ? { max: step.dueDate } : { min: step.startDate }),
          onUpdate: (value) => void update({ [field]: value }),
        }),
      onDelete: () =>
        openModal(DeleteDialog, {
          title: 'Delete step',
          text: `Are you sure you want to delete ${step.name}?`,
          onSubmit: async () => {
            try {
              await step.delete();
            } catch (error) {
              reportFailure(error);
            }
          },
        }),
    },
  });

  return (
    <ContextMenu items={items}>
      <Bar {...props} />
    </ContextMenu>
  );
});
